import React from 'react';

interface BHPHSettingsProps {
  bhphEligible: boolean;
  bhphMinDownType: string;
  bhphMinDownValue: string;
  bhphPaymentAmount: string;
  bhphPaymentFrequency: string;
  bhphTermMonths: string;
  onChange: (field: string, value: string | boolean) => void;
  inputCls: string;
}

const BHPHSettingsSection: React.FC<BHPHSettingsProps> = ({
  bhphEligible,
  bhphMinDownType,
  bhphMinDownValue,
  bhphPaymentAmount,
  bhphPaymentFrequency,
  bhphTermMonths,
  onChange,
  inputCls,
}) => {
  return (
    <div className="border border-gray-200 rounded-lg p-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-700">BHPH Financing Settings</h4>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={bhphEligible}
            onChange={(e) => onChange('bhph_eligible', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
          <span className="ml-2 text-sm text-gray-600">
            {bhphEligible ? 'Eligible' : 'Not Eligible'}
          </span>
        </label>
      </div>

      {bhphEligible && (
        <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-100">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Min Down Payment Type
            </label>
            <div className="flex space-x-3">
              <label className="flex items-center space-x-1 cursor-pointer">
                <input
                  type="radio"
                  name="bhph_min_down_type"
                  value="percent"
                  checked={bhphMinDownType === 'percent'}
                  onChange={(e) => onChange('bhph_min_down_type', e.target.value)}
                  className="h-3.5 w-3.5 text-blue-600"
                />
                <span className="text-sm text-gray-700">% of Price</span>
              </label>
              <label className="flex items-center space-x-1 cursor-pointer">
                <input
                  type="radio"
                  name="bhph_min_down_type"
                  value="fixed"
                  checked={bhphMinDownType === 'fixed'}
                  onChange={(e) => onChange('bhph_min_down_type', e.target.value)}
                  className="h-3.5 w-3.5 text-blue-600"
                />
                <span className="text-sm text-gray-700">Fixed $</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Min Down Payment {bhphMinDownType === 'percent' ? '(%)' : '($)'}
            </label>
            <input
              type="number"
              value={bhphMinDownValue}
              onChange={(e) => onChange('bhph_min_down_value', e.target.value)}
              placeholder={bhphMinDownType === 'percent' ? '20' : '2000'}
              className={inputCls}
              step={bhphMinDownType === 'percent' ? '1' : '100'}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Payment Amount ($)
            </label>
            <input
              type="number"
              value={bhphPaymentAmount}
              onChange={(e) => onChange('bhph_payment_amount', e.target.value)}
              placeholder="250.00"
              className={inputCls}
              step="10"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Payment Frequency
            </label>
            <div className="flex space-x-3">
              <label className="flex items-center space-x-1 cursor-pointer">
                <input
                  type="radio"
                  name="bhph_payment_frequency"
                  value="monthly"
                  checked={bhphPaymentFrequency === 'monthly'}
                  onChange={(e) => onChange('bhph_payment_frequency', e.target.value)}
                  className="h-3.5 w-3.5 text-blue-600"
                />
                <span className="text-sm text-gray-700">Monthly</span>
              </label>
              <label className="flex items-center space-x-1 cursor-pointer">
                <input
                  type="radio"
                  name="bhph_payment_frequency"
                  value="biweekly"
                  checked={bhphPaymentFrequency === 'biweekly'}
                  onChange={(e) => onChange('bhph_payment_frequency', e.target.value)}
                  className="h-3.5 w-3.5 text-blue-600"
                />
                <span className="text-sm text-gray-700">Bi-Weekly</span>
              </label>
            </div>
          </div>

          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Term Length (months)
            </label>
            <input
              type="number"
              value={bhphTermMonths}
              onChange={(e) => onChange('bhph_term_months', e.target.value)}
              placeholder="48"
              className={inputCls}
              min="1"
              max="84"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BHPHSettingsSection;
